import React, { useEffect, useState } from 'react';
import { checkSupabaseConnection, supabaseStatus } from '@/lib/supabase/client';
import { AlertCircle, RefreshCw, CheckCircle } from 'lucide-react';

interface SupabaseStatusProps {
  showInProduction?: boolean;
}

const SupabaseStatus: React.FC<SupabaseStatusProps> = ({
  showInProduction = false,
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  // Only show in development or if explicitly enabled
  const isDevelopment = import.meta.env.DEV;

  const checkConnection = async () => {
    setIsChecking(true);
    await checkSupabaseConnection();
    setLastChecked(new Date());
    setIsChecking(false);
  };

  useEffect(() => {
    if (!isDevelopment && !showInProduction) {
      return;
    }

    checkConnection();
  }, [isDevelopment, showInProduction]);

  if (!isDevelopment && !showInProduction) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-black/80 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-white/10 text-sm">
      <div className="flex items-center gap-2">
        <div className="font-medium">Supabase:</div>
        {isChecking ? (
          <div className="flex items-center">
            <RefreshCw size={16} className="animate-spin mr-1" />
            <span>Checking...</span>
          </div>
        ) : supabaseStatus.isConnected ? (
          <div className="flex items-center text-green-400">
            <CheckCircle size={16} className="mr-1" />
            <span>Connected</span>
          </div>
        ) : (
          <div className="flex items-center text-red-400">
            <AlertCircle size={16} className="mr-1" />
            <span>Error</span>
          </div>
        )}

        <button
          onClick={checkConnection}
          disabled={isChecking}
          className="ml-2 p-1 hover:bg-white/10 rounded"
          title="Check connection"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {!supabaseStatus.isConnected && supabaseStatus.lastError && (
        <div className="mt-1 text-red-400 text-xs max-w-[250px] break-words">
          {supabaseStatus.lastError.message}
        </div>
      )}

      {lastChecked && (
        <div className="mt-1 text-white/50 text-xs">
          Last checked: {lastChecked.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

export default SupabaseStatus;
